import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, Grid, InputLabel, MenuItem, Modal, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ParentPortalLayout from '../components/ParentPortalLayout';
import DetectionClipPlayer, { type ClipFrame } from '../components/DetectionClipPlayer';
import ParentPortalTablePreloader from '../components/ParentPortalPreloader';
import { parentPortalAPI } from '../utils/api';
import type { ChildAttendanceDay, ParentChild } from '../types/parent';
import { parentPortalSolidButtonSx, parentPortalTheme } from '../theme/parentPortalTheme';

const now = new Date();

const formatTime = (value?: string | null) => {
  if (!value) return '-';
  const date = value.includes('T') ? new Date(value) : new Date(`1970-01-01T${value}`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const formatDate = (value: string) => new Date(value).toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

const ParentSnapshots: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentSnapshots' });
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [childId, setChildId] = useState<number | ''>('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [attendance, setAttendance] = useState<ChildAttendanceDay[]>([]);
  const [frames, setFrames] = useState<ClipFrame[]>([]);
  const [clipOpen, setClipOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clipLoading, setClipLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clipError, setClipError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [clipPreviewMode, setClipPreviewMode] = useState(false);

  const monthValue = `${year}-${String(month).padStart(2, '0')}`;
  const years = useMemo(() => Array.from({ length: 5 }, (_, index) => now.getFullYear() - index), []);

  useEffect(() => {
    const loadChildren = async () => {
      try {
        const response = await parentPortalAPI.getChildren();
        const list = response.children || response.data || [];
        setChildren(list);
        setChildId(list[0]?.id || '');
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || t('loadChildrenFailed'));
      }
    };
    loadChildren();
  }, [t]);

  useEffect(() => {
    if (!childId) return;
    const loadAttendance = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await parentPortalAPI.getChildAttendance(Number(childId), monthValue);
        setAttendance(response.attendance || response.data || []);
        setPreviewMode(Boolean(response.preview_mode));
      } catch (err: any) {
        setPreviewMode(false);
        setError(err?.response?.data?.message || err?.message || t('loadAttendanceFailed'));
      } finally {
        setLoading(false);
      }
    };
    loadAttendance();
  }, [childId, monthValue, t]);

  const watchDay = async (date: string) => {
    if (!childId) return;
    setClipOpen(true);
    setClipLoading(true);
    setClipError(null);
    setClipPreviewMode(false);
    setFrames([]);
    try {
      const response = await parentPortalAPI.getChildDetections(Number(childId), date);
      const list = response.frames || response.detections || response.data || [];
      const isPreview = Boolean(response.preview_mode);
      setClipPreviewMode(isPreview);
      const visibleFrames = isPreview ? list.slice(0, 12) : list;
      setFrames(visibleFrames.map(frame => ({
        imageUrl: frame.image_url,
        timestamp: frame.timestamp,
        cameraName: frame.camera_name,
      })));
    } catch (err: any) {
      setClipPreviewMode(false);
      setClipError(err?.response?.data?.message || err?.message || t('loadDetectionsFailed'));
    } finally {
      setClipLoading(false);
    }
  };

  return (
    <ParentPortalLayout title={t('title')}>
      {previewMode && <Alert severity="info" sx={{ mb: 2, borderRadius: 4, bgcolor: parentPortalTheme.preview.bannerBg, color: parentPortalTheme.preview.bannerColor }}>{t('previewBanner')}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('child')}</InputLabel>
              <Select label={t('child')} value={childId} onChange={(event) => setChildId(Number(event.target.value))}>
                {children.map(child => <MenuItem key={child.id} value={child.id}>{child.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('month')}</InputLabel>
              <Select label={t('month')} value={month} onChange={(event) => setMonth(Number(event.target.value))}>
                {Array.from({ length: 12 }, (_, index) => index + 1).map(value => <MenuItem key={value} value={value}>{new Date(2026, value - 1, 1).toLocaleString(undefined, { month: 'long' })}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('year')}</InputLabel>
              <Select label={t('year')} value={year} onChange={(event) => setYear(Number(event.target.value))}>
                {years.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Paper sx={{ overflow: 'hidden', borderRadius: 5, border: `2px solid ${parentPortalTheme.table.border}` }}>
          <Table sx={{
            '& .MuiTableCell-root': { px: { xs: 2.5, sm: 3.5 } },
            '& .MuiTableCell-root:first-of-type': { pl: { xs: 3, sm: 4 } },
            '& .MuiTableCell-root:last-of-type': { pr: { xs: 3, sm: 4 } },
          }}>
            <TableHead sx={{ bgcolor: parentPortalTheme.table.headerBg, '& .MuiTableCell-head': { bgcolor: parentPortalTheme.table.headerBg, color: '#fff' } }}>
              <TableRow>
                <TableCell>{t('date')}</TableCell>
                <TableCell>{t('firstSeen')}</TableCell>
                <TableCell>{t('lastSeen')}</TableCell>
                <TableCell>{t('video')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <ParentPortalTablePreloader columns={4} />
              ) : attendance.map((day, index) => (
                <TableRow key={day.date} sx={{ bgcolor: index % 2 ? parentPortalTheme.table.rowAlt : '#fff' }}>
                  <TableCell>{formatDate(day.date)}</TableCell>
                  <TableCell>{formatTime(day.drop_off_time)}</TableCell>
                  <TableCell>{formatTime(day.pick_up_time)}</TableCell>
                  <TableCell>
                    <Button onClick={() => watchDay(day.date)} sx={{ ...parentPortalSolidButtonSx, px: 3 }}>
                      {t('watch')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && attendance.length === 0 && <TableRow><TableCell colSpan={4} align="center">{t('noRows')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      <Modal open={clipOpen} onClose={() => setClipOpen(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '94vw', md: 900 }, bgcolor: '#fff', borderRadius: 4, p: 3, maxHeight: '92vh', overflow: 'auto' }}>
          <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>{t('clipViewer')}</Typography>
          {clipPreviewMode && <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>{t('previewBanner')}</Alert>}
          <DetectionClipPlayer frames={frames} loading={clipLoading} error={clipError} />
        </Box>
      </Modal>
    </ParentPortalLayout>
  );
};

export default ParentSnapshots;
